/**
 * lib/api/billing.ts -- the client for the W4 read surface.
 *
 * ONE ENDPOINT DECIDES AND REPORTS. GET /api/billing/usage calls the same three
 * functions the gates call -- checkAllowance(), seatAdmission(),
 * storageAdmission() -- rather than re-deriving any of them. So the numbers on
 * the screen ARE the numbers that decide, and this file must never compute a
 * limit, a remainder or a verdict of its own. Everything here is either a type,
 * a formatter, or a sort.
 *
 * THREE THINGS THAT MUST NOT COLLAPSE INTO EACH OTHER, and every type below is
 * shaped to keep them apart:
 *
 *   null on a limit          = UNLIMITED. Rendering it as 0 turns a comped
 *                              account into a locked one.
 *   state: 'unresolved'      = we could not work the norm out. NOT zero used,
 *                              NOT unlimited. Our fault, and it says so.
 *   reason: 'no_owned_account' = this person owns no account. A legitimate
 *                              state for an invited member, not an error.
 *
 * NOTE ON LANGUAGE: every string in this file is English, including the
 * comments -- scripts/check-ui-english.sh scans apps/chaos-ui/src and does not
 * distinguish a comment from a label, deliberately.
 */
import { ApiError, apiJson } from './client';

/** The five metered quantities, in the order the gateway lists them. */
export type AllowanceMetric =
  | 'llm_tokens'
  | 'embed_calls'
  | 'media_embed_units'
  | 'stt_audio_minutes'
  | 'rerank_calls';

/** What a person calls each one. Never the column name. */
export const METRIC_LABEL: Record<AllowanceMetric, string> = {
  llm_tokens: 'Model tokens',
  embed_calls: 'Knowledge indexing',
  media_embed_units: 'Media indexing',
  stt_audio_minutes: 'Speech recognition',
  rerank_calls: 'Search re-ranking',
};

export interface AllowanceRow {
  metric: AllowanceMetric;
  /** 'ok' = the norm was resolved. 'unresolved' = it could not be. */
  state: 'ok' | 'unresolved';
  /** Which of the four unresolvable states, when state is 'unresolved'. */
  refusal: string | null;
  detail: string | null;
  unit: string | null;
  period: 'day' | 'month' | null;
  /** null = UNLIMITED. Never render as zero. */
  included: number | null;
  used: number | null;
  remaining: number | null;
  unlimited: boolean | null;
  windowStart: string | null;
  renewsAt: string | null;
  /** True when the meter is known to be incomplete -- 'at least this much'. */
  usedIsLowerBound: boolean | null;
  unmeasuredEvents: number | null;
  valueOrigin: string | null;
  placeholder: boolean;
}

export type CeilingOutcome =
  | 'allowed'
  | 'unlimited'
  | 'comped'
  | 'would_refuse'
  | 'refused'
  | 'unresolved';

export interface HumanRefusalBody {
  code: string;
  status: number;
  surface: string;
  title: string;
  ranOut: string;
  renewsAt: string | null;
  renewsIn: string | null;
  retryAfterSeconds: number | null;
  whatToDo: string[];
  placeholder: boolean;
  detail: string;
}

export interface SeatsBlock {
  outcome: CeilingOutcome;
  proceed: boolean;
  enforced: boolean;
  /** null = the plan sets no ceiling. Never render it as zero. */
  ceiling: number | null;
  open: number;
  /** false = `open` is not a measurement. Render the words, not the number. */
  measured: boolean;
  reason: string;
  placeholder: boolean;
  refusal: HumanRefusalBody | null;
  /** What the SHADOW probe would have said. null = not measured, not "clean". */
  shadow: { leases: number; wouldRefuse: number; lastCeilingProbe: number | null } | null;
}

export interface StorageBlock {
  outcome: CeilingOutcome;
  proceed: boolean;
  enforced: boolean;
  /** null = unlimited. */
  limitBytes: number | null;
  usedBytes: number;
  /** false = `usedBytes` is not a measurement. */
  measured: boolean;
  reason: string;
  placeholder: boolean;
  refusal: HumanRefusalBody | null;
}

export interface PlanBlock {
  id: string;
  kind: string;
  displayName: string;
  comped: boolean;
  maxConcurrentSlots: number | null;
  maxAlwaysOnSlots: number | null;
  storageBytes: number | null;
  workspaceBaseSlots: number | null;
  workspaceBaseStorageBytes: number | null;
  freeMembersCap: number | null;
  slotPackSlots: number | null;
  storagePackBytes: number | null;
  placeholder: boolean;
  unlimited: { concurrentSlots: boolean; alwaysOnSlots: boolean; storage: boolean };
}

export interface UsageResponse {
  accountId: string | null;
  plan: PlanBlock | null;
  /** 'no_owned_account' | 'plan_unresolved' | 'catalog_unavailable' | null. */
  reason: string | null;
  allowances: AllowanceRow[];
  seats: SeatsBlock | null;
  storage: StorageBlock | null;
  /** Which gates are honoured on the gateway RIGHT NOW, by name. */
  enforcement: { usageAllowances: boolean; seats: boolean; storage: boolean };
  founderPlaceholder: boolean;
  note: string;
  catalogAvailable: boolean;
}

export interface CatalogPrice {
  key: string;
  planId: string | null;
  unitAmountCents: number | null;
  currency: string | null;
  interval: string | null;
  credits: number | null;
  placeholder: boolean;
}

export interface CatalogResponse {
  founderPlaceholder: boolean;
  placeholderCount: number;
  note: string;
  enforcement: string;
  catalogAvailable: boolean;
  catalogUnavailableReason: string | null;
  plans: PlanBlock[];
  prices: CatalogPrice[];
  policy: Array<{
    key: string;
    value: string | number | null;
    unit: string | null;
    placeholder: boolean;
    origin: string;
    canonRef: string | null;
  }>;
  credits: { perVendorUsd: number | null; note: string };
}


// ── contour A: the subscription (M3a/M3b) ───────────────────────────────────
//
// THE SEVEN STATES ARE THE DATABASE'S, not this file's. `chaos.subscription_transitions`
// is the authority on what can happen to a paying account; this type exists so a
// component cannot typo one, not so the client can decide what they are.
//
// TWO OF THEM MEAN "A CHARGE FAILED AND YOU STILL HAVE EVERYTHING", and a screen that
// rendered past_due or grace as a lockout would be lying in the direction that loses
// customers. `rightsPreserved` is the field to read; the state name is what to SHOW.
export type SubscriptionState =
  | 'trialing'
  | 'active'
  | 'past_due'
  | 'grace'
  | 'suspended'
  | 'cancelled'
  | 'comped';

export interface SubscriptionBlock {
  id: string;
  planId: string;
  state: SubscriptionState;
  processor: string;
  processorMode: string | null;
  trialEndsAt: string | null;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  /** When the rights actually stop. Null unless a grace window is running. */
  graceUntil: string | null;
  cancelAtPeriodEnd: boolean;
  dunningAttempts: number;
}

/**
 * What `chaos.account_entitlement()` answers, as the gateway passes it through.
 *
 * `null` on a ceiling means UNLIMITED — 0188's semantics, not a missing value. A
 * component rendering it as 0 turns a comped account into a locked one.
 */
export interface EntitlementBlock {
  accountId: string;
  source: 'subscription' | 'account_plan' | 'absent';
  planId: string;
  kind: string;
  displayName: string;
  comped: boolean;
  maxConcurrentSlots: number | null;
  maxAlwaysOnSlots: number | null;
  storageBytes: number | null;
  workspaceBaseSlots: number | null;
  workspaceBaseStorageBytes: number | null;
  freeMembersCap: number | null;
  slotPackSlots: number | null;
  storagePackBytes: number | null;
  containerMemoryBytes: number | null;
  containerNanoCpus: number | null;
  containerPidsLimit: number | null;
  founderPlaceholder: boolean;
  subscriptionId: string | null;
  state: SubscriptionState | null;
  graceUntil: string | null;
  currentPeriodEnd: string | null;
  rightsPreserved: boolean;
  spawnAllowed: boolean;
  purchaseAllowed: boolean;
  storeOfficialIncluded: boolean;
  asOf: string | null;
}

export interface SubscriptionEventRow {
  fromState: string;
  toState: string;
  trigger: string;
  occurredAt: string;
}

export interface SubscriptionResponse {
  accountId: string | null;
  subscription: SubscriptionBlock | null;
  entitlement: EntitlementBlock | null;
  history: SubscriptionEventRow[];
  /** 'no_owned_account' | 'no_subscription' | null. Three states, not two. */
  reason: string | null;
  provider?: string;
  processorMode?: string;
  /** What may be bought, and why not when not. Resolved by the server. */
  offers?: SubscriptionOffer[];
  /** Whether THIS caller may mark a price test-sellable. The server decides. */
  founderMayMarkDemoPrice?: boolean;
}

export interface InvoiceRow {
  id: string;
  kind: string;
  status: string;
  amountCents: number;
  currency: string;
  processor: string;
  processorMode: string | null;
  planIdSnapshot: string;
  periodStart: string | null;
  periodEnd: string | null;
  errorCode: string | null;
  createdAt: string;
}

export interface InvoicesResponse {
  accountId: string | null;
  invoices: InvoiceRow[];
  reason?: string | null;
}

export interface SubscriptionMoveResponse {
  subscription: SubscriptionBlock | null;
  entitlement: EntitlementBlock | null;
  moved: { from: string | null; to: string | null };
}

/**
 * WHAT A SUBSCRIPTION STATE IS CALLED ON A SCREEN, and the sentence that goes with it.
 *
 * The sentence is here rather than in the component for one reason: the same state must
 * read the same way wherever it appears, and the two states that keep the rights
 * (past_due, grace) are the ones somebody would otherwise paraphrase into a threat.
 * No number appears in any of them — the dates and the ceilings come from the response.
 */
export const SUBSCRIPTION_STATE_LABEL: Record<SubscriptionState, string> = {
  trialing: 'Trial',
  active: 'Active',
  past_due: 'Payment failed',
  grace: 'Grace period',
  suspended: 'Suspended',
  cancelled: 'Cancelled',
  comped: 'Comped',
};

export const SUBSCRIPTION_STATE_SENTENCE: Record<SubscriptionState, string> = {
  trialing: 'You are inside the trial window. Everything the plan includes is available.',
  active: 'Paid and current. Nothing is limited by billing.',
  past_due:
    'A charge did not go through, and NOTHING has been taken away. The processor will try the '
    + 'card again; if it keeps failing you will get a grace period before anything changes.',
  grace:
    'The grace period is running and you still have everything the plan includes. Paying inside '
    + 'this window changes nothing else.',
  suspended:
    'The grace period closed, so this account is on the Free plan\u2019s ceilings. NOTHING has '
    + 'been deleted \u2014 every card, file and Workspace is exactly where it was, and paying '
    + 'brings every ceiling straight back.',
  cancelled:
    'Cancelled. You keep everything the plan includes until the end of the period you have '
    + 'already paid for.',
  comped: 'Comped. This account is never refused for billing reasons.',
};

/** A day, spelled the way a person reads it. No arithmetic against a limit. */
export function saidAsDate(iso: string | null): string {
  if (!iso) return '\u2014';
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '\u2014';
  return at.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

/**
 * Cents as money.
 *
 * The SAME rendering `components/marketplace/marketplace-chrome.tsx::money()` uses, and
 * it is duplicated rather than imported for the reason that file's own header gives: a
 * settings screen importing from the marketplace chrome would make the billing page
 * depend on a module whose whole subject is a different product surface. What must not
 * differ is the OUTPUT, and it does not: both divide by 100 and both let Intl place the
 * symbol.
 */
export function saidAsMoney(cents: number, currency: string): string {
  if (!Number.isFinite(cents)) return '\u2014';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: (currency || 'usd').toUpperCase(),
    }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${(currency || 'usd').toUpperCase()}`;
  }
}

export const billingKeys = {
  usage: ['billing', 'usage'] as const,
  catalog: ['billing', 'catalog'] as const,
  subscription: ['billing', 'subscription'] as const,
  invoices: ['billing', 'invoices'] as const,
};

/**
 * ONE PRICE THIS ACCOUNT MAY BE SOLD, as the server decided.
 *
 * `sellable` is NOT recomputed here. It is a conjunction of server facts — the row is
 * active, it carries a plan, and on the mock provider it is marked as a test price (I18)
 * — and a browser that worked it out would be a second place that rule lives, whose
 * failure mode is a button that looks like it works and refuses.
 */
export interface SubscriptionOffer {
  priceKey: string;
  planId: string;
  unitAmountCents: number;
  currency: string;
  interval: string | null;
  sellable: boolean;
  /** A stable code, never a message. Null when it is sellable. */
  notSellableBecause: string | null;
}

export interface SubscriptionCheckoutResponse {
  subscription: SubscriptionBlock | null;
  checkout?: {
    checkoutRef: string;
    redirectUrl: string;
    mode: string;
    amountMinor: number;
    currency: string;
  };
  /** ALWAYS false. Nothing is paid at the moment a checkout is created. */
  paid?: boolean;
  error?: string;
}

export interface SubscriptionMockSettleResponse {
  delivered: boolean;
  refusal?: string | null;
  eventId?: string;
  subscription?: SubscriptionBlock | null;
}

export const billingApi = {
  usage: () => apiJson<UsageResponse>('/billing/usage'),
  catalog: () => apiJson<CatalogResponse>('/billing/catalog'),
  subscription: () => apiJson<SubscriptionResponse>('/billing/subscription'),
  invoices: () => apiJson<InvoicesResponse>('/billing/invoices'),
  cancel: () =>
    apiJson<SubscriptionMoveResponse>('/billing/subscription/cancel', { method: 'POST' }),
  resume: () =>
    apiJson<SubscriptionMoveResponse>('/billing/subscription/resume', { method: 'POST' }),
  portal: () =>
    apiJson<{ url: string; expiresAt: string }>('/billing/subscription/portal', { method: 'POST' }),
  /**
   * Start a checkout for one price.
   *
   * The PRICE KEY and nothing else. A call that carried an amount would be a call where
   * the customer names what they pay; the server resolves the row and refuses every lock
   * it can know before a hosted session exists.
   */
  checkout: (priceKey: string) =>
    apiJson<SubscriptionCheckoutResponse>('/billing/subscription/checkout', {
      method: 'POST',
      body: JSON.stringify({ priceKey }),
    }),
  /**
   * THE DEMO'S CALLBACK. `mock` only, and it marks nothing: the gateway signs the event
   * the processor would have sent and drives it through the real webhook handler, so the
   * signature check, the dedup row and the one applier all still stand. `outcome` is how
   * the founder's acceptance script breaks a payment on purpose.
   */
  mockSettle: (outcome: 'paid' | 'failed' | 'deleted', sequence?: string) =>
    apiJson<SubscriptionMockSettleResponse>('/billing/subscription/mock-settle', {
      method: 'POST',
      body: JSON.stringify({ outcome, ...(sequence ? { sequence } : {}) }),
    }),
  /**
   * Mark ONE subscription price as sellable by the mock, or unmark it.
   *
   * The instance operator only, and the mock only — the gateway enforces both. It sets
   * one boolean and touches no amount, no currency and no plan, and it exists because
   * the alternative was a `psql` step in the middle of an acceptance script.
   */
  demoPrice: (priceKey: string, mockOnly: boolean) =>
    apiJson<{ priceKey: string; mockOnly: boolean; offers: SubscriptionOffer[] }>(
      '/billing/subscription/demo-price',
      { method: 'POST', body: JSON.stringify({ priceKey, mockOnly }) },
    ),
};

/**
 * `404 feature_disabled` is not an error on this surface.
 *
 * Contour A is off on every box, so the ordinary answer to every call above is a 404
 * with that code — and a screen that rendered it as "could not load" would tell every
 * reader something had broken. `DisabledPanel` and `LoadFailure` are two different
 * panels for exactly this reason, and this is the predicate that tells them apart.
 */
export function isBillingDisabled(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404 && error.code === 'feature_disabled';
}

// ── formatting ──────────────────────────────────────────────────────────────

/**
 * Bytes the way a person reads them. Deliberately the same rendering as the
 * gateway's `saidInBytes()` so a refusal sentence and the meter above it quote
 * the same string for the same number.
 */
export function saidInBytes(n: number): string {
  if (!Number.isFinite(n) || n < 0) return 'unknown';
  if (n < 1024) return `${Math.round(n)} B`;
  const units = ['KiB', 'MiB', 'GiB', 'TiB', 'PiB'];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(1)} ${units[i]}`;
}

/** Big counts with thousands separators; small ones as themselves. */
export function saidInUnits(n: number, unit: string | null): string {
  const num = Number.isFinite(n) ? n.toLocaleString('en-US', { maximumFractionDigits: 2 }) : '?';
  if (!unit) return num;
  return `${num} ${unit}${n === 1 ? '' : 's'}`;
}

/** 'today' / 'this month' / the raw word if the gateway ever adds a third. */
export function periodWord(period: string | null): string {
  if (period === 'day') return 'today';
  if (period === 'month') return 'this month';
  return period ?? '';
}

/**
 * Used / included as a percentage, or null when there is no ratio to take.
 *
 * null is returned for unlimited AND for unresolved AND for a zero norm -- all
 * three of which would otherwise produce a bar. A zero-included metric
 * (media_embed_units on Free is seeded 0) is NOT "0% used", it is "not included
 * in this plan", and the caller has to say so in words.
 */
export function usedFraction(row: AllowanceRow): number | null {
  if (row.state !== 'ok') return null;
  if (row.included === null || row.included <= 0) return null;
  if (row.used === null) return null;
  return Math.min(1, Math.max(0, row.used / row.included));
}

/**
 * WHAT RUNS OUT FIRST -- the question the brief for this screen names, answered
 * over every ceiling at once rather than per panel.
 *
 * Returns the rows sorted by how close each is to its own ceiling, most-spent
 * first, INCLUDING the two level ceilings (seats, storage) alongside the five
 * metered ones. Anything without a ratio -- unlimited, unresolved, or a norm of
 * zero -- is excluded rather than sorted to one end: it has no "how close" and
 * pretending it has one is the whole failure this function exists against.
 */
export interface PressureRow {
  key: string;
  label: string;
  fraction: number;
  detail: string;
}

export function pressureOrder(u: UsageResponse): PressureRow[] {
  const out: PressureRow[] = [];
  for (const row of u.allowances) {
    const f = usedFraction(row);
    if (f === null) continue;
    out.push({
      key: row.metric,
      label: METRIC_LABEL[row.metric] ?? row.metric,
      fraction: f,
      detail: `${saidInUnits(row.used ?? 0, row.unit)} of ${saidInUnits(
        row.included ?? 0,
        row.unit,
      )} ${periodWord(row.period)}`,
    });
  }
  const s = u.seats;
  // `measured === false` is excluded for the same reason `unlimited` is: there
  // is no ratio, and inventing one from an unmeasured zero would rank the
  // account's emptiest-looking ceiling as its safest.
  if (s && s.measured && typeof s.ceiling === 'number' && s.ceiling > 0) {
    out.push({
      key: 'seats',
      label: 'Concurrent cards',
      fraction: Math.min(1, s.open / s.ceiling),
      detail: `${s.open} of ${s.ceiling} running now`,
    });
  }
  const st = u.storage;
  if (st && st.measured && typeof st.limitBytes === 'number' && st.limitBytes > 0) {
    out.push({
      key: 'storage',
      label: 'Stored files',
      fraction: Math.min(1, st.usedBytes / st.limitBytes),
      detail: `${saidInBytes(st.usedBytes)} of ${saidInBytes(st.limitBytes)}`,
    });
  }
  return out.sort((a, b) => b.fraction - a.fraction);
}

/**
 * The four unresolvable states, in words a person can act on. The gateway sends
 * the machine name; turning it into a sentence is a CLIENT concern because the
 * same name means the same thing on every surface.
 */
export const UNRESOLVED_REASON: Record<string, string> = {
  plan_configuration_missing: 'This account has no plan row, so there is no norm to measure against.',
  plan_allowance_unverified: 'This plan has no approved figure for this quantity yet.',
  usage_source_missing: 'Nothing on this deployment records this quantity yet, so it cannot be counted.',
  allowance_table_missing: 'The allowance tables are not present on this deployment.',
};
